import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { guardCreate } from "../context/DuplicateGuard";
import { ANAG_TYPES, displayName } from "../lib/anagConfig";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Database, Copy, Search, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import SimilarContacts from "./SimilarContacts";

function kindOf(atype) {
  const cfg = ANAG_TYPES.find((t) => t.key === atype);
  const isAzienda = cfg && cfg.fields.some((f) => f.key === "denominazione");
  return isAzienda ? "azienda" : "persona";
}

// Campo soggetto con modale "Seleziona da database" / "Aggiungi nuovo" + copia.
export default function SubjectPicker({ atype, value, onChange, label, testId }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("select");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const kind = kindOf(atype);

  const load = async (query) => {
    setLoading(true);
    try { const { data } = await api.get(`/anagrafiche/${atype}?q=${encodeURIComponent(query || "")}`); setItems(data); }
    catch (e) { setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setMode("select"); setQ(""); setForm({}); load(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || mode !== "select") return;
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const choose = (rec) => { onChange(displayName(rec)); setOpen(false); };

  const copy = async () => {
    if (!value) { toast.info("Nessun valore da copiare"); return; }
    try { await navigator.clipboard.writeText(value); toast.success("Copiato negli appunti"); }
    catch { toast.error("Copia non riuscita"); }
  };

  const saveNew = async () => {
    const payload = kind === "azienda"
      ? { denominazione: (form.denominazione || "").trim(), telefono: form.telefono || "", email: form.email || "" }
      : { nome: (form.nome || "").trim(), cognome: (form.cognome || "").trim(), telefono: form.telefono || "", email: form.email || "" };
    const dn = kind === "azienda" ? payload.denominazione : [payload.nome, payload.cognome].filter(Boolean).join(" ");
    if (!dn) { toast.error("Inserisci il nominativo"); return; }
    setSaving(true);
    try {
      const { action, record } = await guardCreate(atype, payload);
      if (action === "created") toast.success("Aggiunto in anagrafica");
      onChange(displayName(record));
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="mt-1.5 flex items-center gap-1.5">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="bg-slate-900/60 border-slate-700 flex-1" />
        <button type="button" onClick={() => setOpen(true)} data-testid={`${testId}-add`} title="Seleziona / Aggiungi"
          className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-slate-700 bg-slate-800/60 text-slate-100 hover:text-amber-400 hover:border-amber-500/40 hover:bg-slate-700/60 transition-colors">
          <Database className="h-4 w-4" />
        </button>
        <button type="button" onClick={copy} data-testid={`${testId}-copy`} title="Copia negli appunti"
          className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-slate-700 bg-slate-800/60 text-slate-100 hover:text-cyan-400 hover:border-cyan-500/40 hover:bg-slate-700/60 transition-colors">
          <Copy className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle className="font-head">{label || "Soggetto"}</DialogTitle></DialogHeader>
          <div className="flex gap-1 border-b border-slate-800">
            <button onClick={() => setMode("select")} data-testid={`${testId}-mode-select`}
              className={`px-3 py-2 text-xs font-semibold tracking-wide border-b-2 -mb-px transition-colors ${mode === "select" ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
              SELEZIONA DA DATABASE
            </button>
            <button onClick={() => setMode("new")} data-testid={`${testId}-mode-new`}
              className={`px-3 py-2 text-xs font-semibold tracking-wide border-b-2 -mb-px transition-colors ${mode === "new" ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
              AGGIUNGI NUOVO
            </button>
          </div>

          {mode === "select" ? (
            <div className="pt-3">
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" data-testid={`${testId}-search`} className="pl-9 h-9 bg-slate-900/60 border-slate-700" />
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-800 rounded-md border border-slate-800">
                {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
                  : items.length === 0 ? <div className="py-6 text-center text-sm text-slate-500">Nessun soggetto in archivio.</div>
                  : items.map((r) => (
                    <button key={r.id} type="button" onClick={() => choose(r)} data-testid={`${testId}-item-${r.id}`}
                      className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/60">
                      {displayName(r)}{r.comune ? <span className="text-slate-500"> · {r.comune}</span> : null}
                    </button>
                  ))}
              </div>
            </div>
          ) : (
            <div className="pt-3 space-y-3">
              {kind === "azienda" ? (
                <div>
                  <Label className="text-slate-300 text-xs">Denominazione *</Label>
                  <Input value={form.denominazione || ""} onChange={(e) => setForm({ ...form, denominazione: e.target.value })} data-testid={`${testId}-new-denominazione`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs">Cognome</Label>
                    <Input value={form.cognome || ""} onChange={(e) => setForm({ ...form, cognome: e.target.value })} data-testid={`${testId}-new-cognome`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Nome</Label>
                    <Input value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid={`${testId}-new-nome`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
                  </div>
                </div>
              )}
              <SimilarContacts atype={atype} nome={form.nome} cognome={form.cognome} denominazione={form.denominazione} onSelect={choose} onCancel={() => setOpen(false)} testId={`${testId}-similar`} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Telefono</Label>
                  <Input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid={`${testId}-new-telefono`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Email</Label>
                  <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${testId}-new-email`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
                </div>
              </div>
              <Button onClick={saveNew} disabled={saving} data-testid={`${testId}-new-save`} className="w-full bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Salva e seleziona</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
